import React, { useCallback, useState } from 'react';
import RuleSection from './RuleSection';
import BlockList from './BlockList';
import GlobalFlags from './GlobalFlags';
import AdjustInstallShave from './AdjustInstallShave';

const GROUPS = [
  {
    key: 'general',
    label: 'Загальні',
    items: [
      {
        key: 'adjust-install-shave',
        kind: 'adjust-install-shave',
        label: 'Adjust install cut',
        hint: 'Android S2S',
        description:
          'Не відправляє вибрану частку callback-attributed Android install у Adjust. Naming і фінальне посилання не змінюються. 0% повністю вимикає зріз.',
      },
      {
        key: 'flags',
        kind: 'flags',
        label: 'Перемикачі',
        hint: 'Поведінка трекера',
        description:
          'Загальні налаштування, що діють на весь трафік. Зараз тут керування тим, що робити з кліком, якщо Binom не відповів через мережевий збій.',
      },
    ],
  },
  {
    key: 'click',
    label: 'За даними кліка',
    items: [
      {
        key: 'sub-id',
        kind: 'block',
        label: 'sub_id_1',
        hint: 'Неймінг зв’язки',
        description:
          'Клік блокується, якщо sub_id_1 містить одне зі значень. Збіг за підрядком, регістр не важливий.',
        title: 'sub_id_1 blocklist',
        endpoint: '/api/sub-id-blocks',
        addPlaceholder: 'test',
      },
      {
        key: 'referer',
        kind: 'block',
        label: 'Referer',
        hint: 'Мітка переходу',
        description:
          'Клік блокується, якщо referer містить одне зі значень. Сюди зазвичай потрапляють мітки модерації.',
        title: 'Referer blocklist',
        endpoint: '/api/referer-blocks',
        addPlaceholder: 'utm_medium=non-organic',
      },
      {
        key: 'ip',
        kind: 'block',
        label: 'IP-адреси',
        hint: 'Підмережі та діапазони',
        description:
          'Клік відсікається, якщо його IP потрапляє в один із діапазонів. Приймає одиночні адреси, підмережі у форматі CIDR і діапазони через дефіс. Працює для IPv4 та IPv6.',
        title: 'IP blocklist',
        endpoint: '/api/ip-blocks',
        multiline: true,
        addPlaceholder: '8.34.208.0/20\n34.0.0.0-34.0.227.255\n13.211.45.54',
        addHint: 'Можна вставити список — по одному правилу в рядку',
      },
      {
        key: 'isp',
        kind: 'block',
        label: 'Провайдери',
        hint: 'Власник IP',
        description:
          'Ріже IP дата-центрів і хостингів. Значення звіряється з назвою організації за підрядком: google спіймає і Google LLC, і Google Cloud.',
        title: 'ISP blocklist',
        endpoint: '/api/isp-blocks',
        addPlaceholder: 'google',
      },
    ],
  },
  {
    key: 'device',
    label: 'За пристроєм',
    items: [
      {
        key: 'black',
        kind: 'rule',
        label: 'Заборонені',
        hint: 'Android ID',
        description:
          'Пристрої, яким заборонено отримувати посилання. Поповнюється вручну і автоматично при клоакінгу.',
        config: {
          type: 'black',
          title: 'Android ID blacklist',
          placeholder: 'Android ID to block',
          addVariant: 'danger',
          showSourceFilter: true,
        },
      },
      {
        key: 'white',
        kind: 'rule',
        label: 'Довірені',
        hint: 'Android ID',
        description:
          'Пристрій повністю минає фільтри за IP: провайдер, гео, Proxy/VPN і Trust score не застосовуються. Для своїх тестових телефонів.',
        config: {
          type: 'white',
          title: 'Android ID whitelist',
          placeholder: 'Android ID to whitelist',
          addVariant: 'success',
          showSourceFilter: false,
        },
      },
    ],
  },
];

const ITEMS = GROUPS.flatMap((group) => group.items);

export default function SettingsPanel({ onUnauthorized }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [activeKey, setActiveKey] = useState(ITEMS[0].key);

  const handleError = useCallback(
    (err) => {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      console.error('[Settings] request failed:', err);
      const msg = err?.response?.data?.error || err.message || 'Unknown error';
      window.alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    },
    [onUnauthorized]
  );

  const handleMutated = useCallback(() => setRefreshToken((token) => token + 1), []);

  const active = ITEMS.find((item) => item.key === activeKey) || ITEMS[0];
  const isSharedRule = active.kind === 'block' || active.kind === 'rule';

  return (
    <div className="settings-page mt-3">
      <nav className="settings-nav" aria-label="Налаштування">
        {GROUPS.map((group) => (
          <div className="settings-nav-group" key={group.key}>
            <div className="settings-nav-label">{group.label}</div>
            <div className="settings-nav-items">
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`settings-nav-btn${activeKey === item.key ? ' is-active' : ''}`}
                    onClick={() => setActiveKey(item.key)}
                    aria-current={activeKey === item.key ? 'page' : undefined}
                  >
                    <span className="settings-nav-btn-label">{item.label}</span>
                    <span className="settings-nav-btn-hint">{item.hint}</span>
                  </button>
                ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="settings-intro">
        <p className="settings-intro-text">{active.description}</p>
        <p className="settings-intro-note">
          {isSharedRule
            ? 'Правила спільні для всіх додатків.'
            : 'Налаштування застосовується до відповідного трафіку після збереження.'}
        </p>
      </div>

      <section aria-label={active.label}>
        {active.kind === 'adjust-install-shave' ? (
          <AdjustInstallShave onError={handleError} />
        ) : active.kind === 'flags' ? (
          <GlobalFlags onError={handleError} />
        ) : active.kind === 'block' ? (
          <BlockList
            key={active.key}
            onError={handleError}
            title={active.title}
            endpoint={active.endpoint}
            addPlaceholder={active.addPlaceholder}
            multiline={active.multiline}
            addHint={active.addHint}
          />
        ) : (
          <RuleSection
            key={active.key}
            config={active.config}
            onError={handleError}
            onMutated={handleMutated}
            refreshToken={refreshToken}
          />
        )}
      </section>
    </div>
  );
}
